import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchAllCrmDeals, type AggregatedDeal } from "@/lib/crmAggregate";
import { CrmEmptyState } from "@/components/crm/CrmEmptyState";
import { RecordLinkRow } from "@/components/crm/RecordLinkRow";
import { formatCrmStatusLabel } from "@/lib/crmDisplay";

export function CrmDealsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").trim().toLowerCase();
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deals, setDeals] = useState<AggregatedDeal[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await fetchAllCrmDeals(200);
        if (!cancelled) setDeals(rows);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Could not load deals.");
          setDeals([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredDeals = useMemo(
    () =>
      deals.filter((deal) =>
        !query
          ? true
          : [deal.name, deal.accountId, deal.accountName, deal.stage, deal.status, String(deal.amount ?? ""), deal.ownerId]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(query)),
      ),
    [deals, query],
  );

  const onSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchInput.trim()) next.set("q", searchInput.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Deals</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Monitor pipeline stages, forecast value, and progression toward close.
          </p>
        </div>
        <Link
          to="/crm/deals/new"
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
        >
          Create new deal
        </Link>
      </div>

      <form onSubmit={onSearch} className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search deals by name, account, stage, status, or value"
          className="w-full max-w-xl rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <button
          type="submit"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/10"
        >
          Search
        </button>
      </form>

      {loading ? <p className="text-sm text-slate-500">Loading deals…</p> : null}
      {error ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200">
          CRM deals are unavailable. {error}
        </div>
      ) : null}

      {!loading && !error && filteredDeals.length === 0 && !query ? (
        <CrmEmptyState
          title="No deals yet"
          description="Track stage, value, and close date on an account. Create an account first if your pipeline is empty."
          primaryCta={{ href: "/crm/deals/new", label: "Create your first deal" }}
          secondaryCta={{ href: "/crm/accounts/new", label: "Create an account" }}
        />
      ) : null}

      {!loading && (filteredDeals.length > 0 || query || error) ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/10">
              <tr>
                <th className="px-4 py-3 font-semibold">Deal</th>
                <th className="px-4 py-3 font-semibold">Account</th>
                <th className="px-4 py-3 font-semibold">Stage</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Amount</th>
                <th className="px-4 py-3 font-semibold">Close date</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.map((deal) => {
                const href = `/crm/deals/${encodeURIComponent(deal.id)}?accountId=${encodeURIComponent(deal.accountId)}`;
                return (
                  <RecordLinkRow key={`${deal.accountId}-${deal.id}`} href={href}>
                    <td className="px-4 py-3">
                      <Link to={href} className="text-indigo-700 hover:underline dark:text-indigo-300">
                        {deal.name || deal.id}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/crm/accounts/${encodeURIComponent(deal.accountId)}`}
                        className="text-indigo-700 hover:underline dark:text-indigo-300"
                        data-row-ignore
                      >
                        {deal.accountName || deal.accountId}
                      </Link>
                    </td>
                    <td className="px-4 py-3 capitalize">{formatCrmStatusLabel(deal.stage) || "—"}</td>
                    <td className="px-4 py-3 capitalize">{formatCrmStatusLabel(deal.status) || "—"}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {deal.amount != null ? deal.amount.toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {deal.closeDate ? new Date(deal.closeDate).toLocaleDateString() : "—"}
                    </td>
                  </RecordLinkRow>
                );
              })}
              {filteredDeals.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                    {query ? "No matching deals found." : "No deals yet."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
